import { Component, Input, Output, EventEmitter, OnInit, inject, signal, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageTemplateService, MessageTemplate } from '../../core/services/message-template.service';
import { CorrectiveActionTemplateService, TemplateResponse, CorrectiveActionTemplate } from '../../core/services/corrective-action-template.service';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { takeUntil, Subject } from 'rxjs';
import { TemplateCreateModalComponent } from '../template-create-modal/template-create-modal.component';
import { NewsTemplateModalComponent } from '../news-template-modal/news-template-modal.component';
import { ActionTemplateModalComponent } from '../action-template-modal/action-template-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-message',
  standalone: true,
  imports: [CommonModule, FormsModule, TemplateCreateModalComponent, NewsTemplateModalComponent, ActionTemplateModalComponent, ConfirmModalComponent, TranslatePipe],
  templateUrl: './message.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './message.component.scss'
})
export class MessageComponent implements OnInit, OnDestroy {
  private templateService = inject(MessageTemplateService);
  private actionTemplateService = inject(CorrectiveActionTemplateService);
  private session = inject(SessionService);
  private toastService = inject(ToastService);
  private destroy$ = new Subject<void>();

  emailTemplates = signal<MessageTemplate[]>([]);
  newsTemplates = signal<MessageTemplate[]>([]);
  selectedEmailTemplate = signal<MessageTemplate | null>(null);
  selectedNewsTemplate = signal<MessageTemplate | null>(null);
  actionTemplates = signal<CorrectiveActionTemplate[]>([]);
  selectedActionTemplate = signal<CorrectiveActionTemplate | null>(null);
  @Input() messageAction = '';
  @Input() titreAction = '';

  @Output() messageActionChange = new EventEmitter<string>();
  @Output() titreActionChange = new EventEmitter<string>();
  showTemplateModal = false;
  showNewsTemplateModal = false;
  showActionTemplateModal = false;
  showDeleteConfirm = false;
  templateToDelete: MessageTemplate | CorrectiveActionTemplate | null = null;
  @Input() message = '';
  @Input() messagePatient = '';
  @Input() sujetPatient = '';
  @Input() titrePatient = '';
  @Input() titreRapide = '';

  @Output() messageChange = new EventEmitter<string>();
  @Output() messagePatientChange = new EventEmitter<string>();
  @Output() sujetPatientChange = new EventEmitter<string>();
  @Output() titrePatientChange = new EventEmitter<string>();
  @Output() titreRapideChange = new EventEmitter<string>();

  ngOnInit() {
    this.loadTemplates();
  }

  private loadTemplates() {
    const establishment = this.getEstablishment();
    if (!establishment) return;

    this.templateService.getTemplatesByType('EMAIL_REPLY', establishment.org_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.emailTemplates.set(templates);
          // Auto-select first template
          if (templates.length > 0) {
            this.titrePatient = templates[0].name;
            this.messagePatient = templates[0].content;
            this.sujetPatient = templates[0].subject || '';
            this.titrePatientChange.emit(templates[0].name);
            this.messagePatientChange.emit(templates[0].content);
            this.sujetPatientChange.emit(templates[0].subject || '');
            this.selectedEmailTemplate.set(templates[0]);
          }
        },
        error: (err) => {
          console.error('Error loading email templates:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Impossible de charger les templates email');
        }
      });

    this.templateService.getTemplatesByType('NEWS_FEED', establishment.org_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.newsTemplates.set(templates);
          // Auto-select first template
          if (templates.length > 0) {
            this.titreRapide = templates[0].name;
            this.message = templates[0].content;
            this.titreRapideChange.emit(templates[0].name);
            this.messageChange.emit(templates[0].content);
            this.selectedNewsTemplate.set(templates[0]);
          }
        },
        error: (err) => {
          console.error('Error loading news templates:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Impossible de charger les templates actualité');
        }
      });

    this.actionTemplateService.getTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.actionTemplates.set(templates);
          // Auto-select first template
          if (templates.length > 0) {
            this.titreAction = templates[0].name;
            this.messageAction = templates[0].description;
            this.titreActionChange.emit(templates[0].name);
            this.messageActionChange.emit(templates[0].description);
            this.selectedActionTemplate.set(templates[0]);
          }
        },
        error: (err) => {
          console.error('Error loading action templates:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Impossible de charger les templates actions');
        }
      });
  }

  onMessageChange(value: string) {
    this.messageChange.emit(value);
  }

  onMessagePatientChange(value: string) {
    this.messagePatientChange.emit(value);
  }

  onSujetPatientChange(value: string) {
    this.sujetPatientChange.emit(value);
  }

  onTitrePatientChange(value: string) {
    this.titrePatientChange.emit(value);

    const template = this.emailTemplates().find(t => t.name.toLowerCase() === value.toLowerCase());
    if (template) {
      this.selectedEmailTemplate.set(template);
      this.sujetPatient = template.subject || '';
      this.messagePatient = template.content;
      this.messagePatientChange.emit(template.content);
      this.sujetPatientChange.emit(template.subject || '');
    } else {
      this.selectedEmailTemplate.set(null);
      this.updateMessagePatientText();
    }
  }

  onTitreRapideChange(value: string) {
    this.titreRapideChange.emit(value);

    const template = this.newsTemplates().find(t => t.name.toLowerCase() === value.toLowerCase());
    if (template) {
      this.selectedNewsTemplate.set(template);
      this.messageChange.emit(template.content);
    } else {
      this.selectedNewsTemplate.set(null);
      this.updateMessageRapideText();
    }
  }

  clearTitrePatient() {
    const template = this.selectedEmailTemplate();
    if (template) {
      this.templateToDelete = template;
      this.showDeleteConfirm = true;
    } else {
      this.titrePatient = '';
      this.titrePatientChange.emit('');
    }
  }

  clearTitreRapide() {
    const template = this.selectedNewsTemplate();
    if (template) {
      this.templateToDelete = template;
      this.showDeleteConfirm = true;
    } else {
      this.titreRapide = '';
      this.titreRapideChange.emit('');
    }
  }

  private getEstablishment() {
    const selectedId = this.session.selectedId();
    const establishment = this.session.establishments().find(e => e.id === selectedId);
    if (!establishment?.org_id) {
      this.toastService.show('Erreur', 'error', 3000, 'Organisation non trouvée');
      return null;
    }
    return { ...establishment, org_id: establishment.org_id! };
  }

  saveMessagePatient() {
    const establishment = this.getEstablishment();
    if (!establishment) return;

    const template = this.selectedEmailTemplate();
    if (template) {
      this.templateService.updateTemplate(template.id, {
        organisationId: establishment.org_id,
        name: this.titrePatient || template.name,
        subject: template.subject || undefined,
        content: this.messagePatient
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Mettre à jour le template dans la liste locale
          const updatedTemplate: MessageTemplate = {
            ...template,
            name: this.titrePatient || template.name,
            subject: template.subject || null,
            content: this.messagePatient
          };
          this.selectedEmailTemplate.set(updatedTemplate);
          this.emailTemplates.update(templates =>
            templates.map(t => t.id === template.id ? updatedTemplate : t)
          );
          this.toastService.show('Sauvegardé', 'success', 3000, 'Template patient mis à jour');
        },
        error: (err) => {
          console.error('Template save error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la sauvegarde');
        }
      });
    } else {
      this.templateService.createTemplate({
        organisationId: establishment.org_id,
        name: this.titrePatient || 'Nouveau template',
        subject: 'Réponse patient',
        content: this.messagePatient,
        type: 'EMAIL_REPLY'
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newTemplate) => {
          this.selectedEmailTemplate.set(newTemplate);
          this.emailTemplates.update(templates => [...templates, newTemplate]);
          this.toastService.show('Créé', 'success', 3000, 'Nouveau template créé');
        },
        error: (err) => {
          console.error('Template create error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la création');
        }
      });
    }
  }

  saveMessageRapide() {
    const establishment = this.getEstablishment();
    if (!establishment) return;

    const template = this.selectedNewsTemplate();
    if (template) {
      this.templateService.updateTemplate(template.id, {
        organisationId: establishment.org_id,
        name: this.titreRapide || template.name,
        content: this.message
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Mettre à jour le template dans la liste locale
          const updatedTemplate: MessageTemplate = {
            ...template,
            name: this.titreRapide || template.name,
            content: this.message
          };
          this.selectedNewsTemplate.set(updatedTemplate);
          this.newsTemplates.update(templates =>
            templates.map(t => t.id === template.id ? updatedTemplate : t)
          );
          this.toastService.show('Sauvegardé', 'success', 3000, 'Template rapide mis à jour');
        },
        error: (err) => {
          console.error('Template save error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la sauvegarde');
        }
      });
    } else {
      this.templateService.createTemplate({
        organisationId: establishment.org_id,
        name: this.titreRapide || 'Nouveau template',
        content: this.message,
        type: 'NEWS_FEED'
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newTemplate) => {
          this.selectedNewsTemplate.set(newTemplate);
          this.newsTemplates.update(templates => [...templates, newTemplate]);
          this.toastService.show('Créé', 'success', 3000, 'Nouveau template créé');
        },
        error: (err) => {
          console.error('Template create error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la création');
        }
      });
    }
  }

  createMessagePatient() {
    this.showTemplateModal = true;
  }

  onMessageActionChange(value: string) {
    this.messageActionChange.emit(value);
  }

  onTitreActionChange(value: string) {
    this.titreActionChange.emit(value);

    const template = this.actionTemplates().find(t => t.name.toLowerCase() === value.toLowerCase());
    if (template) {
      this.selectedActionTemplate.set(template);
      this.messageAction = template.description;
      this.messageActionChange.emit(template.description);
    } else {
      this.selectedActionTemplate.set(null);
    }
  }

  clearTitreAction() {
    const template = this.selectedActionTemplate();
    if (template) {
      this.templateToDelete = template;
      this.showDeleteConfirm = true;
    } else {
      this.titreAction = '';
      this.titreActionChange.emit('');
    }
  }

  saveActionTemplate() {
    const template = this.selectedActionTemplate();
    if (template) {
      this.actionTemplateService.updateTemplate(template.id, {
        name: this.titreAction || template.name,
        title: this.titreAction || template.name,
        description: this.messageAction,
        triggerReason: template.trigger_reason || 'Action corrective'
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Mettre à jour le template dans la liste locale
          const updatedTemplate: CorrectiveActionTemplate = {
            ...template,
            title: this.titreAction || template.title,
            description: this.messageAction
          };
          this.selectedActionTemplate.set(updatedTemplate);
          this.actionTemplates.update(templates =>
            templates.map(t => t.id === template.id ? updatedTemplate : t)
          );
          this.toastService.show('Sauvegardé', 'success', 3000, 'Template action mis à jour');
        },
        error: (err) => {
          console.error('Template save error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la sauvegarde');
        }
      });
    } else {
      this.actionTemplateService.createTemplate({
        title: this.titreAction || 'Nouveau template action',
        name: this.titreAction || 'Nouveau template action',
        description: this.messageAction,
        category: 'general',
        priority: 'medium',
        estimated_duration: 1,
        steps: []
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const newTemplate: CorrectiveActionTemplate = {
            id: response.id,
            title: this.titreAction || 'Nouveau template action',
            name: this.titreAction || 'Nouveau template action',
            description: this.messageAction,
            category: 'general',
            priority: 'medium',
            estimated_duration: 1,
            steps: []
          };
          this.selectedActionTemplate.set(newTemplate);
          this.actionTemplates.update(templates => [...templates, newTemplate]);
          this.toastService.show('Créé', 'success', 3000, 'Nouveau template créé');
        },
        error: (err) => {
          console.error('Template create error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la création');
        }
      });
    }
  }

  createMessageRapide() {
    this.showNewsTemplateModal = true;
  }

  createActionTemplate() {
    this.showActionTemplateModal = true;
  }

  updateMessagePatientText() {
    // Templates now loaded from backend only
  }

  updateMessageRapideText() {
    // Templates now loaded from backend only
  }

  private sanitizeForLog(input: any): string {
    if (input == null) return 'null';
    const str = String(input);
    return str.replace(/[\r\n\t]/g, ' ').substring(0, 200);
  }

  onTemplateCreated(template: MessageTemplate) {
    this.emailTemplates.update(templates => [...templates, template]);

    // Sélectionner automatiquement le nouveau template créé
    this.titrePatient = template.name;
    this.messagePatient = template.content;
    this.sujetPatient = template.subject || '';
    this.titrePatientChange.emit(template.name);
    this.messagePatientChange.emit(template.content);
    this.sujetPatientChange.emit(template.subject || '');
    this.selectedEmailTemplate.set(template);

    this.showTemplateModal = false;
    this.toastService.show('Créé', 'success', 3000, 'Template créé avec succès');
  }

  onTemplateModalClose() {
    this.showTemplateModal = false;
  }

  onNewsTemplateModalClose() {
    this.showNewsTemplateModal = false;
  }

  onNewsTemplateCreated(template: MessageTemplate) {
    this.newsTemplates.update(templates => [...templates, template]);

    // Sélectionner automatiquement le nouveau template créé
    this.titreRapide = template.name;
    this.message = template.content;
    this.titreRapideChange.emit(template.name);
    this.messageChange.emit(template.content);
    this.selectedNewsTemplate.set(template);

    this.showNewsTemplateModal = false;
    this.toastService.show('Créé', 'success', 3000, 'Template créé avec succès');
  }

  onActionTemplateModalClose() {
    this.showActionTemplateModal = false;
  }

  onActionTemplateCreated(template: CorrectiveActionTemplate) {
    this.actionTemplates.update(templates => [...templates, template]);

    // Sélectionner automatiquement le nouveau template créé
    this.titreAction = template.name;
    this.messageAction = template.description;
    this.titreActionChange.emit(template.name);
    this.messageActionChange.emit(template.description);
    this.selectedActionTemplate.set(template);

    this.showActionTemplateModal = false;
    this.toastService.show('Créé', 'success', 3000, 'Template action créé avec succès');
  }

  confirmDeleteTemplate() {
    if (!this.templateToDelete) return;

    if (this.templateToDelete.hasOwnProperty('triggerReason')) {
      // C'est un template d'action corrective
      const actionTemplate = this.templateToDelete as any;
      this.actionTemplateService.deleteTemplate(actionTemplate.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastService.show('Supprimé', 'success', 3000, 'Template supprimé');
            this.actionTemplates.update(templates =>
              templates.filter(t => t.id !== actionTemplate.id)
            );

            // Sélectionner le premier template restant
            const remainingTemplates = this.actionTemplates();
            if (remainingTemplates.length > 0) {
              const firstTemplate = remainingTemplates[0];
              this.titreAction = firstTemplate.name;
              this.messageAction = firstTemplate.description;
              this.titreActionChange.emit(firstTemplate.name);
              this.messageActionChange.emit(firstTemplate.description);
              this.selectedActionTemplate.set(firstTemplate);
            } else {
              this.titreAction = '';
              this.messageAction = '';
              this.titreActionChange.emit('');
              this.messageActionChange.emit('');
              this.selectedActionTemplate.set(null);
            }

            this.showDeleteConfirm = false;
            this.templateToDelete = null;
          },
          error: (err) => {
            console.error('Delete error:', this.sanitizeForLog(err));
            this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la suppression');
            this.showDeleteConfirm = false;
            this.templateToDelete = null;
          }
        });
      return;
    }

    // C'est un MessageTemplate classique
    const establishment = this.getEstablishment();
    if (!establishment) return;

    const messageTemplate = this.templateToDelete as MessageTemplate;
    this.templateService.deleteTemplate(messageTemplate.id, establishment.org_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show('Supprimé', 'success', 3000, 'Template supprimé');

          // Remove from local signals instead of reloading
          if (messageTemplate.templateType === 'EMAIL_REPLY') {
            this.emailTemplates.update(templates =>
              templates.filter(t => t.id !== messageTemplate.id)
            );

            // Sélectionner le premier template restant
            const remainingTemplates = this.emailTemplates();
            if (remainingTemplates.length > 0) {
              const firstTemplate = remainingTemplates[0];
              this.titrePatient = firstTemplate.name;
              this.messagePatient = firstTemplate.content;
              this.sujetPatient = firstTemplate.subject || '';
              this.titrePatientChange.emit(firstTemplate.name);
              this.messagePatientChange.emit(firstTemplate.content);
              this.sujetPatientChange.emit(firstTemplate.subject || '');
              this.selectedEmailTemplate.set(firstTemplate);
            } else {
              this.titrePatient = '';
              this.sujetPatient = '';
              this.messagePatient = '';
              this.titrePatientChange.emit('');
              this.messagePatientChange.emit('');
              this.sujetPatientChange.emit('');
              this.selectedEmailTemplate.set(null);
            }
          } else {
            this.newsTemplates.update(templates =>
              templates.filter(t => t.id !== messageTemplate.id)
            );

            // Sélectionner le premier template restant
            const remainingTemplates = this.newsTemplates();
            if (remainingTemplates.length > 0) {
              const firstTemplate = remainingTemplates[0];
              this.titreRapide = firstTemplate.name;
              this.message = firstTemplate.content;
              this.titreRapideChange.emit(firstTemplate.name);
              this.messageChange.emit(firstTemplate.content);
              this.selectedNewsTemplate.set(firstTemplate);
            } else {
              this.titreRapide = '';
              this.message = '';
              this.titreRapideChange.emit('');
              this.messageChange.emit('');
              this.selectedNewsTemplate.set(null);
            }
          }

          this.showDeleteConfirm = false;
          this.templateToDelete = null;
        },
        error: (err) => {
          console.error('Delete error:', this.sanitizeForLog(err));
          this.toastService.show('Erreur', 'error', 3000, 'Erreur lors de la suppression');
          this.showDeleteConfirm = false;
          this.templateToDelete = null;
        }
      });
  }

  cancelDeleteTemplate() {
    this.showDeleteConfirm = false;
    this.templateToDelete = null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
